import type { UpdateMePayload } from "@/api/auth/authPrivateApi";
import { User } from "@/types/user";

export type AuthType = "password" | "nca" | "third_party";
export type UserRole = "student" | "teacher" | "manager";

export const persistedUserTypeFromRole = (role: UserRole): User["user_type"] =>
  role === "manager" ? "teacher" : role;

// Build server-compatible payload for POST /students
export const buildCreateStudentPayload = (u: User) => {
  return {
    id: null,
    user_id: null,
    department_id: u.department_id ?? null,
    specialization_id: u.specialization_id ?? null,
    start_activity_year: u.start_activity_year ?? null,
    end_activity_year: u.end_activity_year ?? null,
    user: {
      REQUEST_CONTINUE: 0,
      REQUEST_REDUNDANT: 1,
      REQUEST_SKIP: 2,
      id: null,
      school_id: u.school_id ?? null,
      auth_type: (u.auth_type as AuthType | undefined) ?? "password",
      group: null,
      photo: u.photo ?? null,
      photo_thumb: u.photo_thumb ?? {},
      color: u.color ?? null,
      firstname: u.firstname ?? "",
      lastname: u.lastname ?? "",
      patronymic: u.patronymic ?? null,
      email: u.email ?? null,
      phone: u.phone ?? null,
      username: (u.username ?? "").toString().trim() ? (u.username ?? "").toString().trim() : undefined,
      description: u.description ?? null,
      password: u.password ?? null,
      school: {
        REQUEST_CONTINUE: 0,
        REQUEST_REDUNDANT: 1,
        REQUEST_SKIP: 2,
        id: null,
        type: null,
        name: null,
        logo: null,
        logo_thumb: {},
        website: null,
        email: null,
      },
      register_date: null,
      last_activity_date: null,
      additional: (u as any).additional ?? {
        almaty_daryn_school_id: null,
        almaty_daryn_teacher_name: null,
        almaty_daryn_class_name: null,
      },
      is_online: false,
      is_need_complete_challenge: false,
      is_multiple_schools: false,
    },
    credibility: u.credibility ?? 0,
    points: u.points ?? 0,
    attempts: [],
    results: [],
    scores: [],
    reviewer_results: [],
    reviewer_scores: [],
    notify_via_sms: Boolean(u.notify_via_sms ?? false),
    notify_via_email: u.notify_via_email ?? 1,
    request_photo_challenge: Boolean(u.request_photo_challenge ?? false),
  };
};

/**
 * Payload для PUT /user.json: эндпоинт принимает объект целиком, поэтому
 * неизменяемые поля берутся из снимка загруженного профиля, а редактируемые
 * передаются через overrides. Так «Сохранить» не тащит с собой пароль,
 * «Изменить пароль» — несохранённые правки формы, а подстановка Ф.И.О. перед
 * скачиванием диплома — вообще ничего лишнего.
 */
export const buildUpdateMePayload = (
  snapshot: Record<string, unknown>,
  overrides: Partial<UpdateMePayload> = {}
): UpdateMePayload => {
  const snap = snapshot as any;

  return {
    id: Number(snap.id),
    school_id: snap.school_id ?? null,
    auth_type: null,
    group: null,
    photo: snap.photo ?? null,
    photo_thumb: snap.photo_thumb ?? {},
    color: snap.color ?? null,
    firstname: snap.firstname ?? null,
    lastname: snap.lastname ?? null,
    patronymic: snap.patronymic ?? null,
    email: snap.email ?? null,
    phone: snap.phone ?? null,
    username: snap.username ?? null,
    description: snap.description ?? null,
    password: null,
    school: snap.school ?? {},
    register_date: snap.register_date ?? null,
    last_activity_date: snap.last_activity_date ?? null,
    additional: snap.additional ?? {},
    is_online: Boolean(snap.is_online),
    is_need_complete_challenge: Boolean(snap.is_need_complete_challenge),
    is_multiple_schools: Boolean(snap.is_multiple_schools),
    ...overrides,
  };
};
